// src/pages/TechnicianNotifications.js
import React, { useState, useEffect } from 'react';
import {
  collection, query, where, orderBy,
  onSnapshot, updateDoc, doc, writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

const TYPE_LABELS = {
  photo_flagged:      'Photo Flagged',
  supervisor_message: 'Message',
  service_rejected:   'Correction Needed',
  announcement:       'Announcement',
  journey_assigned:   'Journey Plan'
};

function TechnicianNotifications({ user, onBack }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filter, setFilter]               = useState('all');

  const unreadCount = notifications.filter(n => !n.read).length;

  // ── Real-time listener — only this technician's notifications ─────────────
  useEffect(() => {
    const q = query(
      collection(db, 'technicianNotifications'),
      where('recipientId', '==', user.id),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, (snap) => {
      setNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return () => unsub();
  }, [user.id]);

  // ── Mark single as read ───────────────────────────────────────────────────
  const markRead = async (notif) => {
    if (notif.read) return;
    try {
      await updateDoc(doc(db, 'technicianNotifications', notif.id), { read: true });
    } catch (e) {}
  };

  // ── Mark all as read ──────────────────────────────────────────────────────
  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.read);
    if (!unread.length) return;
    try {
      const batch = writeBatch(db);
      unread.forEach(n =>
        batch.update(doc(db, 'technicianNotifications', n.id), { read: true })
      );
      await batch.commit();
    } catch (e) {}
  };

  const formatTimeAgo = (ts) => {
    if (!ts) return '';
    const d   = ts.toDate ? ts.toDate() : new Date(ts);
    const sec = Math.floor((new Date() - d) / 1000);
    if (sec < 60)    return 'Just now';
    if (sec < 3600)  return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return d.toLocaleDateString('en-KE', { day: 'numeric', month: 'short' });
  };

  const filtered = notifications.filter(n => {
    if (filter === 'all')    return true;
    if (filter === 'unread') return !n.read;
    return n.type === filter;
  });

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>

      {/* Header */}
      <div style={s.header}>
        <button style={s.backBtn} onClick={onBack}>← Back</button>
        <div style={s.headerCenter}>
          <h1 style={s.headerTitle}>🔔 Notifications</h1>
          {unreadCount > 0 && (
            <span style={s.unreadBadge}>{unreadCount} unread</span>
          )}
        </div>
        {unreadCount > 0 && (
          <button style={s.markAllBtn} onClick={markAllRead}>✓ All read</button>
        )}
      </div>

      {/* Filter pills */}
      <div style={s.filterRow}>
        {[
          { key: 'all',               label: `All (${notifications.length})`                             },
          { key: 'unread',            label: `Unread (${unreadCount})`                                   },
          { key: 'photo_flagged',     label: `🚩 Flagged (${notifications.filter(n=>n.type==='photo_flagged').length})`     },
          { key: 'supervisor_message',label: `💬 Messages (${notifications.filter(n=>n.type==='supervisor_message').length})` },
          { key: 'announcement',      label: `📢 Announcements (${notifications.filter(n=>n.type==='announcement').length})`  },
          { key: 'service_rejected',  label: `❌ Corrections (${notifications.filter(n=>n.type==='service_rejected').length})`},
          { key: 'journey_assigned',  label: `🗺️ Journey (${notifications.filter(n=>n.type==='journey_assigned').length})`    }
        ].map(f => (
          <button
            key={f.key}
            style={{
              ...s.filterPill,
              ...(filter === f.key ? s.filterActive : {})
            }}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div style={s.list}>
        {loading ? (
          <div style={s.centered}>
            <div style={s.spinner} />
            <p style={{ color: 'rgba(0,0,0,0.4)', marginTop: 12 }}>Loading...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={s.empty}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>
              {filter === 'unread' ? '✅' : '🔕'}
            </div>
            <h3 style={s.emptyTitle}>
              {filter === 'unread' ? 'All caught up!' : 'No notifications'}
            </h3>
            <p style={s.emptyText}>
              {filter === 'unread'
                ? 'You have no unread notifications'
                : 'Notifications from your supervisor will appear here'}
            </p>
          </div>
        ) : (
          filtered.map(notif => (
            <NotifCard
              key={notif.id}
              notif={notif}
              onRead={() => markRead(notif)}
              formatTimeAgo={formatTimeAgo}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ── Individual notification card ──────────────────────────────────────────────
function NotifCard({ notif, onRead, formatTimeAgo }) {
  const [expanded, setExpanded] = useState(false);

  const handleClick = () => {
    onRead();
    setExpanded(prev => !prev);
  };

  const isUrgent = notif.type === 'photo_flagged' ||
                   notif.type === 'service_rejected' ||
                   notif.type === 'emergency_service';

  return (
    <div
      style={{
        ...cs.card,
        borderLeft: `4px solid ${notif.read ? 'rgba(0,0,0,0.08)' : (notif.color || '#AB2D2A')}`,
        background:  notif.read ? '#fff' : '#fef9f9',
        boxShadow:   notif.read
          ? '0 1px 4px rgba(0,0,0,0.06)'
          : `0 2px 12px ${notif.color || '#AB2D2A'}22`
      }}
      onClick={handleClick}
    >
      {/* Top row */}
      <div style={cs.topRow}>
        {/* Icon */}
        <div style={{
          ...cs.icon,
          background: `${notif.color || '#AB2D2A'}18`,
          border:     `1px solid ${notif.color || '#AB2D2A'}30`
        }}>
          <span style={{ fontSize: 22 }}>{notif.icon || '📋'}</span>
        </div>

        {/* Content */}
        <div style={cs.content}>
          <div style={cs.titleRow}>
            <span style={{
              ...cs.typePill,
              background: `${notif.color || '#AB2D2A'}18`,
              color:       notif.color || '#AB2D2A'
            }}>
              {TYPE_LABELS[notif.type] || 'Alert'}
            </span>
            {!notif.read && (
              <span style={{
                ...cs.urgentDot,
                background: notif.color || '#AB2D2A'
              }} />
            )}
            {isUrgent && !notif.read && (
              <span style={cs.urgentPill}>Action needed</span>
            )}
          </div>
          <p style={cs.title}>{notif.title}</p>
          <p style={{
            ...cs.message,
            WebkitLineClamp: expanded ? 'unset' : 2,
            overflow:        expanded ? 'visible' : 'hidden'
          }}>
            {notif.message}
          </p>
          <div style={cs.footer}>
            <span style={cs.time}>{formatTimeAgo(notif.createdAt)}</span>
            <span style={cs.expandHint}>
              {expanded ? '▲ Less' : '▼ More'}
            </span>
          </div>
        </div>
      </div>

      {/* Expanded detail — show meta info */}
      {expanded && notif.meta && (
        <div style={cs.detail}>
          {notif.meta.outletName && (
            <div style={cs.detailRow}>
              <span style={cs.detailKey}>Outlet</span>
              <span style={cs.detailVal}>{notif.meta.outletName}</span>
            </div>
          )}
          {notif.meta.orderNumber && (
            <div style={cs.detailRow}>
              <span style={cs.detailKey}>Service #</span>
              <span style={cs.detailVal}>#{notif.meta.orderNumber}</span>
            </div>
          )}
          {notif.meta.photoType && (
            <div style={cs.detailRow}>
              <span style={cs.detailKey}>Photo Type</span>
              <span style={cs.detailVal}>{notif.meta.photoType}</span>
            </div>
          )}
          {notif.meta.reason && (
            <div style={cs.detailRow}>
              <span style={cs.detailKey}>Reason</span>
              <span style={cs.detailVal}>{notif.meta.reason}</span>
            </div>
          )}
          {notif.meta.flaggedBy && (
            <div style={cs.detailRow}>
              <span style={cs.detailKey}>Flagged by</span>
              <span style={cs.detailVal}>{notif.meta.flaggedBy}</span>
            </div>
          )}
          {notif.meta.rejectedBy && (
            <div style={cs.detailRow}>
              <span style={cs.detailKey}>Reviewed by</span>
              <span style={cs.detailVal}>{notif.meta.rejectedBy}</span>
            </div>
          )}
          {notif.meta.senderName && (
            <div style={cs.detailRow}>
              <span style={cs.detailKey}>From</span>
              <span style={cs.detailVal}>{notif.meta.senderName}</span>
            </div>
          )}
          {notif.meta.date && (
            <div style={cs.detailRow}>
              <span style={cs.detailKey}>Date</span>
              <span style={cs.detailVal}>{notif.meta.date}</span>
            </div>
          )}
          {notif.meta.stopCount && (
            <div style={cs.detailRow}>
              <span style={cs.detailKey}>Stops</span>
              <span style={cs.detailVal}>{notif.meta.stopCount}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Page styles ───────────────────────────────────────────────────────────────
const s = {
  page: {
    minHeight:  '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  header: {
    background:    '#fff',
    padding:       '16px 20px',
    display:       'flex',
    alignItems:    'center',
    gap:           12,
    boxShadow:     '0 2px 8px rgba(0,0,0,0.08)',
    position:      'sticky',
    top:           0,
    zIndex:        100,
    borderBottom:  '3px solid transparent',
    borderImage:   'linear-gradient(90deg, transparent, #AB2D2A 20%, #C0392B 50%, #AB2D2A 80%, transparent) 1'
  },
  backBtn: {
    background:   'rgba(171,45,42,0.08)',
    border:       '1px solid rgba(171,45,42,0.2)',
    borderRadius: 10,
    padding:      '8px 14px',
    color:        '#AB2D2A',
    fontSize:     14,
    fontWeight:   600,
    cursor:       'pointer',
    flexShrink:   0
  },
  headerCenter: {
    flex:        1,
    display:     'flex',
    alignItems:  'center',
    gap:         10
  },
  headerTitle: {
    margin:     0,
    fontSize:   18,
    fontWeight: 700,
    color:      '#1e293b'
  },
  unreadBadge: {
    background:   '#AB2D2A',
    color:        '#fff',
    fontSize:     11,
    fontWeight:   700,
    padding:      '3px 10px',
    borderRadius: 20
  },
  markAllBtn: {
    background:   'rgba(171,45,42,0.08)',
    border:       '1px solid rgba(171,45,42,0.2)',
    borderRadius: 10,
    padding:      '8px 14px',
    color:        '#AB2D2A',
    fontSize:     13,
    fontWeight:   600,
    cursor:       'pointer',
    flexShrink:   0
  },
  filterRow: {
    display:    'flex',
    gap:        8,
    padding:    '12px 16px',
    overflowX:  'auto',
    background: '#fff',
    borderBottom: '1px solid rgba(0,0,0,0.06)'
  },
  filterPill: {
    background:   'rgba(0,0,0,0.05)',
    border:       '1px solid rgba(0,0,0,0.1)',
    borderRadius: 20,
    padding:      '6px 14px',
    fontSize:     12,
    fontWeight:   600,
    color:        '#666',
    cursor:       'pointer',
    whiteSpace:   'nowrap',
    flexShrink:   0
  },
  filterActive: {
    background:  'rgba(171,45,42,0.12)',
    borderColor: 'rgba(171,45,42,0.3)',
    color:       '#AB2D2A'
  },
  list: {
    padding:       '16px',
    display:       'flex',
    flexDirection: 'column',
    gap:           10,
    paddingBottom: 100
  },
  centered: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        '60px 20px'
  },
  spinner: {
    width:        36,
    height:       36,
    border:       '3px solid rgba(171,45,42,0.2)',
    borderTop:    '3px solid #AB2D2A',
    borderRadius: '50%',
    animation:    'spin 0.8s linear infinite'
  },
  empty: {
    textAlign: 'center',
    padding:   '60px 20px'
  },
  emptyTitle: { fontSize: 18, fontWeight: 700, color: '#334155', margin: '0 0 8px' },
  emptyText:  { fontSize: 14, color: '#94a3b8', margin: 0 }
};

// ── Card styles ───────────────────────────────────────────────────────────────
const cs = {
  card: {
    borderRadius: 14,
    padding:      '14px 16px',
    cursor:       'pointer',
    transition:   'all 0.2s ease'
  },
  topRow: {
    display: 'flex',
    gap:     12
  },
  icon: {
    width:          48,
    height:         48,
    borderRadius:   12,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    flexShrink:     0
  },
  content: { flex: 1, minWidth: 0 },
  titleRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        6,
    marginBottom: 4,
    flexWrap:   'wrap'
  },
  typePill: {
    fontSize:      10,
    fontWeight:    700,
    padding:       '2px 8px',
    borderRadius:  6,
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  urgentDot: {
    width:        7,
    height:       7,
    borderRadius: '50%'
  },
  urgentPill: {
    fontSize:     10,
    fontWeight:   700,
    color:        '#AB2D2A',
    background:   'rgba(171,45,42,0.1)',
    padding:      '2px 7px',
    borderRadius: 6
  },
  title: {
    margin:     '0 0 3px',
    fontSize:   14,
    fontWeight: 700,
    color:      '#1e293b'
  },
  message: {
    margin:          '0 0 6px',
    fontSize:        13,
    color:           '#64748b',
    lineHeight:      1.5,
    display:         '-webkit-box',
    WebkitBoxOrient: 'vertical'
  },
  footer: {
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'center'
  },
  time:       { fontSize: 11, color: '#94a3b8' },
  expandHint: { fontSize: 11, color: '#AB2D2A', fontWeight: 600 },
  detail: {
    marginTop:    12,
    paddingTop:   12,
    borderTop:    '1px solid rgba(0,0,0,0.06)',
    display:      'flex',
    flexDirection: 'column',
    gap:          6
  },
  detailRow: {
    display:  'flex',
    gap:      8,
    fontSize: 13
  },
  detailKey: {
    color:      '#94a3b8',
    fontWeight: 600,
    minWidth:   90,
    flexShrink: 0
  },
  detailVal: {
    color:      '#334155',
    fontWeight: 500
  }
};

export default TechnicianNotifications;
